// @flow
import { Map } from "immutable"

import type { BookingId } from "mmb/types/primitiveTypes"
import {
  setBillingDetails,
  // Composed actions
  initStates,
  loadSuccessStates,
  updateSuccessStates,
  saveSuccessStates,
} from "./states/actions"
import {
  setAccepted,
  // Composed
  loadSuccessPrices,
  updateSuccessPrices,
  saveSuccessPrices,
} from "./prices/actions"
import { setInfantsFareSupported, setBillingDetailsForcedByZooz } from "./utils/actions"
import { setJourney } from "./journey/actions"
import { setShadowJourney } from "./shadowJourney/actions"
import { loadSuccessBaggage } from "./baggage/actions"
import { setAdditionalServices } from "./additionalServices/actions"
import { setInfantsConditions } from "./infantsConditions/actions"
import Journey from "common/records/Journey"
import dangerouslySetReduxFormValue from "./reservation/actions/dangerouslySetReduxFormValue"
import { insuranceLoaded } from "booking/services/additionalServices/services/insurance/actions"
import { RESERVATION_BILLING_DETAILS_TYPES } from "../scenes/ReservationForm/consts/reservationConsts"
import type { Passengers } from "booking/services/reservation/types"
import type { InsurancePricing } from "./prices/reducer"
import type { InfantsConditions } from "./infantsConditions/reducer"
import type { DocumentNeed } from "common/services/document/mapperDocumentNeed"
import type { InsuranceNumber } from "common/consts/insurance"
import type { PassengerName } from "./states/reducer"
import type { Ancillary, AncillaryPerSegment } from "booking/services/ancillaries3rd/actions"
import type { AdditionalServicesInput } from "booking/services/additionalServices/types"

// Additional services
import { setAncillaryServices } from "booking/services/ancillaries3rd/services/actions"
import type { Baggage } from "booking/services/baggage/types"

// eslint-disable-next-line flowtype/no-weak-types
type Dispatch = Object => void // Consider some redux own type

// Init
// ===
export type Init = {
  // States
  bookingToken: string,
  backToSearchUrl: string,
  passengers: Passengers,
  passengersNames: Array<PassengerName>,
  email: string,
  price: number,
  handBags: number[],
  holdBags: number[],
  insurance: Array<InsuranceNumber>,
  multistep: boolean,
  activeStep: number,
  promocode: ?string,
  searchBags: ?string,
  // Prices
  accepted: Map,
  // Utils
}
export const init = (args: Init) => (dispatch: Dispatch) => {
  dispatch(
    initStates({
      bookingToken: args.bookingToken,
      backToSearchUrl: args.backToSearchUrl,
      multistep: args.multistep,
      activeStep: args.activeStep,
      originals: {
        originalPassengers: args.passengers,
        originalPassengersNames: args.passengersNames,
        originalEmail: args.email,
        originalPrice: args.price,
        originalHandBags: args.handBags,
        originalHoldBags: args.holdBags,
        originalInsurance: args.insurance,
        originalPromocode: args.promocode,
        searchBags: args.searchBags,
      },
    }),
  )
  dispatch(setAccepted(args.accepted))
}

// Additional Services
// ===
export type AdditionalServices = {
  paid_checkin?: Ancillary,
  airhelp_plus?: Ancillary,
  fast_track?: Ancillary,
  blueribbon_bags?: AncillaryPerSegment,
}

export const additionalServicesLoaded = (as: AdditionalServices) => (dispatch: Dispatch) => {
  dispatch(setAncillaryServices(as))
}

// Load success
// ===
export type LoadSuccess = {
  // Additional Services
  additionalServices: AdditionalServicesInput,
  // States
  bookingToken: string,
  maxPassengers: number,
  documentNeed: DocumentNeed,
  flightsChecked: boolean,
  adultThreshold: number,
  handBagsWithPrioBoardingAllowed: boolean,
  // Journey
  journey: Journey,
  shadowJourney: Journey,
  // Baggage
  baggage: Baggage,
  // Prices
  insurancePricing: InsurancePricing,
  basePrice: number,
  adultsPrice: number,
  childrenPrice: number,
  infantsPrice: number,
  fees: Map,
  gracePeriodFee: number,
  accepted: Map,
  holdBagsPricingCurrent: Map<string, number>,
  // InfantsConditions
  infantsConditions: InfantsConditions,
  // Utils
  infantsFareSupported: boolean,
  refFlags: Array<string>,
}
export const loadSuccess = (args: LoadSuccess) => (dispatch: Dispatch) => {
  dispatch(
    // $FlowFixMe
    loadSuccessStates({
      bookingToken: args.bookingToken,
      maxPassengers: args.maxPassengers,
      documentNeed: args.documentNeed,
      flightsChecked: args.flightsChecked,
      refFlags: args.refFlags,
      thresholds: {
        // TODO: take from new api once ready
        // adult: args.adultThreshold, // e.g. 18
        // teen: args.teenThreshold, // e.g. 16
        adult: args.adultThreshold,
        teen: args.adultThreshold,
        child: 2,
      },
      handBagsWithPrioBoardingAllowed: args.handBagsWithPrioBoardingAllowed,
    }),
  )
  dispatch(setJourney(args.journey))
  dispatch(setShadowJourney(args.shadowJourney))
  dispatch(loadSuccessBaggage(args.baggage))
  dispatch(insuranceLoaded(args.insurancePricing))
  dispatch(
    loadSuccessPrices({
      basePrice: args.basePrice,
      adultsPrice: args.adultsPrice,
      childrenPrice: args.childrenPrice,
      infantsPrice: args.infantsPrice,
      fees: args.fees,
      gracePeriodFee: args.gracePeriodFee,
      accepted: args.accepted,
      holdBagsPricingCurrent: args.holdBagsPricingCurrent,
    }),
  )
  dispatch(setInfantsConditions(args.infantsConditions))
  dispatch(setInfantsFareSupported(args.infantsFareSupported))

  // Additional Services
  if (args.additionalServices) {
    dispatch(setAdditionalServices(args.additionalServices))
  }
}

// Update success
export type UpdateSuccess = {
  // States
  bookingToken: string,
  flightsChecked: boolean,
  // Prices
  fees: Map,
  basePrice: number,
  adultsPrice: number,
  childrenPrice: number,
  infantsPrice: number,
  accepted: Map,
  journey: Map,
}
export const updateSuccess = (args: UpdateSuccess) => (dispatch: Dispatch) => {
  dispatch(
    updateSuccessStates({
      bookingToken: args.bookingToken,
      flightsChecked: args.flightsChecked,
    }),
  )
  dispatch(
    updateSuccessPrices({
      fees: args.fees,
      basePrice: args.basePrice,
      adultsPrice: args.adultsPrice,
      childrenPrice: args.childrenPrice,
      infantsPrice: args.infantsPrice,
      accepted: args.accepted,
    }),
  )
  if (args.journey) {
    dispatch(setJourney(args.journey))
  }
}

// Save success
export type SaveSuccess = {
  // States
  bookingId: BookingId,
  authToken: string,
  sandbox: boolean,
  passengers: any, // eslint-disable-line flowtype/no-weak-types
  // Utils
  contact: any, // eslint-disable-line flowtype/no-weak-types
  // Prices
  shameBagsPrice: number,
  shameFinalPriceInCurrency: number,
}
export const saveSuccess = (args: SaveSuccess) => (dispatch: Dispatch) => {
  dispatch(
    saveSuccessStates({
      bookingId: args.bookingId,
      authToken: args.authToken,
      sandbox: args.sandbox,
      passengers: args.passengers,
    }),
  )
  dispatch(
    saveSuccessPrices({
      shameBagsPrice: args.shameBagsPrice,
      shameFinalPriceInCurrency: args.shameFinalPriceInCurrency,
    }),
  )
}

// Open billing details forced by zooz
export const openBillingDetailsForcedByZooz = () => (dispatch: Dispatch) => {
  dispatch(setBillingDetails(true))
  dispatch(setBillingDetailsForcedByZooz(true))
  dispatch(
    dangerouslySetReduxFormValue({
      field: "billingDetails.type",
      value: RESERVATION_BILLING_DETAILS_TYPES.PERSON,
    }),
  )
}
